import { useEffect, useMemo, useState } from "react";

const API_BASE_URL = "https://backend-p9wj.onrender.com/api/products";

const initialFormState = {
  name: "",
  type: "Savings",
  apy: "",
  minimumBalance: "",
  monthlyFee: "",
  affiliateLink: "",
  perks: [],
};

export default function AdminDashboard() {
  const [formData, setFormData] = useState(initialFormState);
  const [products, setProducts] = useState([]);
  const [perkInput, setPerkInput] = useState("");
  const [adminKey, setAdminKey] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await fetch(API_BASE_URL);
        if (!response.ok) {
          throw new Error(`Fetch failed: ${response.status}`);
        }
        const data = await response.json();
        setProducts(data);
      } catch (error) {
        console.error("Failed to load products:", error);
      }
    };

    fetchProducts();
  }, []);

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleAddPerk = () => {
    const trimmed = perkInput.trim();
    if (!trimmed) return;
    setFormData((prev) => ({
      ...prev,
      perks: [...prev.perks, trimmed],
    }));
    setPerkInput("");
  };

  const handleRemovePerk = (perkToRemove) => {
    setFormData((prev) => ({
      ...prev,
      perks: prev.perks.filter((perk) => perk !== perkToRemove),
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const payload = {
      name: formData.name,
      type: formData.type,
      apy: parseFloat(formData.apy) || 0,
      minimumBalance: parseFloat(formData.minimumBalance) || 0,
      monthlyFee: parseFloat(formData.monthlyFee) || 0,
      affiliateLink: formData.affiliateLink,
      perks: formData.perks,
    };

    try {
      const endpoint = isEditing ? `${API_BASE_URL}/${editingId}` : API_BASE_URL;
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          "x-admin-api-key": adminKey,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Request failed: ${response.status}`);
      }

      const result = await response.json();

      if (isEditing) {
        setProducts((prev) => prev.map((product) => (product._id === editingId ? result : product)));
      } else {
        setProducts((prev) => [result, ...prev]);
      }

      setFormData(initialFormState);
      setAdminKey("");
      setIsEditing(false);
      setEditingId(null);
    } catch (error) {
      console.error("Save failed:", error);
    }
  };

  const handleEdit = (product) => {
    setFormData({
      name: product.name,
      type: product.type,
      apy: product.apy.toString(),
      minimumBalance: product.minimumBalance.toString(),
      monthlyFee: product.monthlyFee.toString(),
      affiliateLink: product.affiliateLink,
      perks: product.perks,
    });
    setIsEditing(true);
    setEditingId(product._id);
  };

  const handleDelete = async (productId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/${productId}`, {
        method: "DELETE",
        headers: {
          "x-admin-api-key": adminKey,
        },
      });

      if (!response.ok) {
        throw new Error(`Delete failed: ${response.status}`);
      }

      setProducts((prev) => prev.filter((product) => product._id !== productId));

      if (editingId === productId) {
        setFormData(initialFormState);
        setIsEditing(false);
        setEditingId(null);
      }
    } catch (error) {
      console.error("Delete failed:", error);
    }
  };

  const productCountLabel = useMemo(
    () => `${products.length} product${products.length === 1 ? "" : "s"}`,
    [products.length]
  );

  return (
    <div className="min-h-[calc(100vh-80px)] px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8 rounded-[2rem] border border-border/70 bg-card/80 p-8 shadow-[0_30px_80px_-40px_rgba(15,23,42,0.45)] backdrop-blur-2xl">
          <div className="max-w-3xl">
            <p className="text-sm font-semibold uppercase tracking-[0.24em] text-secondary-foreground/80">
              Admin Dashboard
            </p>
            <h1 className="mt-3 text-4xl font-semibold leading-tight text-foreground sm:text-5xl">
              Manage CRAM Finance product listings.
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground">
              Create, edit, and remove bank products with a secure admin interface. The live product list updates instantly for internal review.
            </p>
          </div>
        </div>

        <div className="grid gap-8 xl:grid-cols-[1.2fr_0.95fr]">
          <section className="rounded-[2rem] border border-border/70 bg-card/80 p-8 shadow-[0_24px_70px_-48px_rgba(15,23,42,0.55)] backdrop-blur-2xl">
            <div className="mb-6 flex items-center justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Create or update a product</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Enter bank product details that match your secure backend schema.
                </p>
              </div>
              <span className="rounded-full bg-secondary/10 px-3 py-1.5 text-sm text-secondary-foreground">
                {productCountLabel}
              </span>
            </div>

            <form className="space-y-6" onSubmit={handleSubmit}>
              <div className="rounded-[1.75rem] border border-border/60 bg-muted/10 p-5">
                <p className="text-sm font-semibold text-foreground">Admin Passkey</p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Use the secure key required for creating, updating, and deleting products.
                </p>
                <input
                  type="password"
                  value={adminKey}
                  onChange={(event) => setAdminKey(event.target.value)}
                  placeholder="Enter admin passkey"
                  className="mt-4 w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </div>

              <div className="grid gap-6 sm:grid-cols-2">
                <label className="block space-y-2 text-sm text-foreground">
                  <span className="font-medium">Name</span>
                  <input
                    value={formData.name}
                    onChange={handleChange("name")}
                    placeholder="Ally Bank"
                    className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>

                <label className="block space-y-2 text-sm text-foreground">
                  <span className="font-medium">Type</span>
                  <select
                    value={formData.type}
                    onChange={handleChange("type")}
                    className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="Savings">Savings</option>
                    <option value="Checking">Checking</option>
                    <option value="Credit Card">Credit Card</option>
                  </select>
                </label>
              </div>

              <div className="grid gap-6 sm:grid-cols-3">
                <label className="block space-y-2 text-sm text-foreground">
                  <span className="font-medium">APY</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.apy}
                    onChange={handleChange("apy")}
                    placeholder="4.5"
                    className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>

                <label className="block space-y-2 text-sm text-foreground">
                  <span className="font-medium">Minimum Balance</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.minimumBalance}
                    onChange={handleChange("minimumBalance")}
                    placeholder="0"
                    className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>

                <label className="block space-y-2 text-sm text-foreground">
                  <span className="font-medium">Monthly Fee</span>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.monthlyFee}
                    onChange={handleChange("monthlyFee")}
                    placeholder="0"
                    className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                </label>
              </div>

              <label className="block space-y-2 text-sm text-foreground">
                <span className="font-medium">Affiliate Link</span>
                <input
                  type="url"
                  value={formData.affiliateLink}
                  onChange={handleChange("affiliateLink")}
                  placeholder="https://tracking.link/..."
                  className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                />
              </label>

              <div className="space-y-4 rounded-3xl border border-border/70 bg-muted/10 p-4">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-foreground">Perks</p>
                    <p className="text-xs text-muted-foreground">Add feature tags for the product.</p>
                  </div>
                  <button
                    type="button"
                    onClick={handleAddPerk}
                    className="inline-flex items-center justify-center rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90"
                  >
                    Add perk
                  </button>
                </div>

                <div className="grid gap-3 sm:grid-cols-[1fr_auto]">
                  <input
                    value={perkInput}
                    onChange={(event) => setPerkInput(event.target.value)}
                    placeholder="No ATM fees"
                    className="w-full rounded-2xl border border-border/70 bg-background px-4 py-3 text-sm text-foreground outline-none transition focus:border-primary focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    type="button"
                    onClick={handleAddPerk}
                    className="rounded-2xl bg-secondary px-4 py-3 text-sm font-semibold text-secondary-foreground transition hover:bg-secondary/90"
                  >
                    Add
                  </button>
                </div>

                <div className="grid gap-2">
                  {formData.perks.length ? (
                    <div className="flex flex-wrap gap-2">
                      {formData.perks.map((perk) => (
                        <span
                          key={perk}
                          className="inline-flex items-center gap-2 rounded-full border border-border/80 bg-background px-3 py-2 text-sm text-foreground"
                        >
                          {perk}
                          <button
                            type="button"
                            onClick={() => handleRemovePerk(perk)}
                            className="text-muted-foreground transition hover:text-destructive"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No perks added yet.</p>
                  )}
                </div>
              </div>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {isEditing ? "You are editing an existing product." : "Create a new product for the admin catalog."}
                </p>
                <button
                  type="submit"
                  className="inline-flex items-center justify-center rounded-2xl bg-foreground px-6 py-3 text-sm font-semibold text-background transition hover:shadow-lg"
                >
                  {isEditing ? "Save changes" : "Create product"}
                </button>
              </div>
            </form>
          </section>

          <section className="rounded-[2rem] border border-border/70 bg-card/80 p-8 shadow-[0_24px_70px_-48px_rgba(15,23,42,0.55)] backdrop-blur-2xl">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h2 className="text-2xl font-semibold text-foreground">Live product list</h2>
                <p className="mt-2 text-sm text-muted-foreground">
                  Review all products and manage edits from the dashboard.
                </p>
              </div>
              <span className="rounded-full bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                {productCountLabel}
              </span>
            </div>

            <div className="space-y-4">
              {products.length === 0 ? (
                <div className="rounded-3xl border border-dashed border-border/60 bg-background/90 p-6 text-center text-sm text-muted-foreground">
                  No products are available yet. Add a new product to populate the live list.
                </div>
              ) : (
                products.map((product) => (
                  <div
                    key={product._id}
                    className="rounded-3xl border border-border/70 bg-background/90 p-5 shadow-sm"
                  >
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-lg font-semibold text-foreground">{product.name}</p>
                        <p className="mt-1 text-sm text-muted-foreground">
                          {product.type} · {product.apy}% APY · ${product.monthlyFee} / month
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleEdit(product)}
                          className="rounded-2xl border border-border/70 bg-secondary/10 px-4 py-2 text-sm font-semibold text-secondary-foreground transition hover:bg-secondary/20"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(product._id)}
                          className="rounded-2xl bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition hover:bg-destructive/90"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="mt-4 grid gap-2 sm:grid-cols-2">
                      <div className="text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground">Minimum Balance:</span> ${product.minimumBalance}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        <span className="font-semibold text-foreground">Affiliate Link:</span>{" "}
                        <a
                          href={product.affiliateLink}
                          target="_blank"
                          rel="noreferrer"
                          className="text-primary hover:underline"
                        >
                          Apply at {product.name}
                        </a>
                      </div>
                    </div>

                    {product.perks.length > 0 && (
                      <ul className="mt-4 flex flex-wrap gap-2 text-sm text-foreground">
                        {product.perks.map((perk) => (
                          <li
                            key={perk}
                            className="rounded-full bg-muted/20 px-3 py-2"
                          >
                            {perk}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
